import axios from "axios";
import api from "@/shared/services/apiClient";

/**
 * Step 1: Request Pre-signed URL
 * Server builds unified S3 key: uploads/${business_unit}/${relationType}/${relationId}/${filename}
 * business_unit is derived from the authenticated user's JWT on the server.
 */
export const getPresignedUrl = async (
  fileName: string,
  fileType: string,
  relationType: string,
  relationId: string | number,
) => {
  const response = await api.post("/attachments/generate-url", {
    fileName,
    fileType,
    relationType,
    relationId: String(relationId),
  });
  const { uploadUrl, s3Key, bucketName } = response.data;
  if (!uploadUrl || !s3Key) {
    throw new Error("Invalid response from Pre-signed URL generator");
  }
  return { uploadUrl, s3Key, bucketName };
};

export const uploadToS3Direct = async (
  url: string,
  file: File,
  mimeType: string,
  onProgress?: (percent: number) => void,
) => {
  const response = await axios.put(url, file, {
    headers: { "Content-Type": mimeType },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total,
        );
        onProgress(percentCompleted);
      }
    },
  });
  return response;
};

export const confirmAttachmentInDB = async (payload: any) => {
  const response = await api.post("/attachments/confirm", payload);
  return response.data;
};

/**
 * Attach a copy of an existing SharePoint file to a relation.
 * The backend does a server-side S3 copy — no bytes pass through the client.
 */
export const copySharepointFileToRelation = async (
  fileId: string,
  relationType: string,
  relationId: string | number,
) => {
  const response = await api.post("/attachments/copy-from-sharepoint", {
    file_id: fileId,
    relation_type: relationType,
    relation_id: String(relationId),
  });
  return response.data;
};

export const linkSharepointFileToChat = async (
  fileId: string,
  channelId: string,
) => {
  const response = await api.post("/attachments/chat-link-from-sharepoint", {
    file_id: fileId,
    channel_id: channelId,
  });
  return response.data;
};

export const deleteAttachmentFromAll = async (attachmentId: string | number) => {
  const response = await api.delete(`/attachments/${attachmentId}`);
  return response.data;
};

export const getAttachmentViewingUrl = async (id: number | string) => {
  const response = await api.get(`/attachments/view/${id}`);
  return response.data.url;
};

/**
 * Downloads attachment bytes via our backend proxy (avoids S3 CORS).
 * Returns a File ready for re-upload (e.g. to Stream's CDN).
 */
export const proxyDownloadAsFile = async (
  id: number | string,
  displayName: string,
  mimeType?: string,
): Promise<File> => {
  const response = await api.get(`/attachments/proxy/${id}`, { responseType: "blob" });
  const blob: Blob = response.data;
  return new File([blob], displayName, { type: mimeType || blob.type });
};
